import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  readPricebookBranchesRefusal,
  updatePricebookBranches,
} from "../../../../services";
import {
  PriceBookBranchScope,
  PricebookBranch,
  PricebookBranches,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import ConfirmModal from "../../../ConfirmModal";
import { ALL_BRANCHES_LABEL, GROUP_RADIUS } from "../constants";
import {
  buildBranchesRequest,
  buildBranchesWithShopsMessage,
  initialCheckedCompanyIds,
  isBranchLocked,
} from "./branchSelection";

const BRANCH_LIST_MAX_HEIGHT = 360;

const DRAFT_NOTICE = "สมุดราคาฉบับร่างใช้ได้เฉพาะ 00-HO เปลี่ยนสาขาไม่ได้";
const SAVE_FAILED = "บันทึกสาขาไม่สำเร็จ กรุณาลองอีกครั้ง";
const HEAD_OFFICE_HINT = "00-HO เปิดใช้เสมอ";
const HAS_SHOPS_HINT = "มีร้านค้าผูกอยู่ ถอดที่หน้าลูกค้าก่อน";

const toBranchScope = (value: string): PriceBookBranchScope =>
  value === "all" ? "all" : "selected";

interface BranchDialogProps {
  open: boolean;
  pricebookUuid: string;
  branches: PricebookBranches | null;
  onClose: () => void;
  /** The API's answer: every branch with the book's new scope and shop counts. */
  onSaved: (branches: PricebookBranches) => void;
}

/**
 * Which branches' shops may be put on the book: every branch, or 00-HO plus the ticked ones.
 * 00-HO and every branch with shops on the book stay ticked; a draft cannot leave 00-HO.
 */
export default function BranchDialog({
  open,
  pricebookUuid,
  branches,
  onClose,
  onSaved,
}: BranchDialogProps) {
  const [branchScope, setBranchScope] = useState<PriceBookBranchScope>("all");
  const [checkedCompanyIds, setCheckedCompanyIds] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!open || !branches) return;
    setBranchScope(branches.branch_scope);
    setCheckedCompanyIds(initialCheckedCompanyIds(branches));
    setErrorMessage("");
  }, [open, branches]);

  const isDraft = Boolean(branches?.is_draft);
  const isReadOnly = isDraft || saving;
  const branchList = branches?.branches ?? [];

  const handleChangeScope = (event: ChangeEvent<HTMLInputElement>) => {
    setBranchScope(toBranchScope(event.target.value));
  };

  const handleToggleBranch = (companyId: string, isChecked: boolean) => {
    setCheckedCompanyIds((prev) =>
      isChecked
        ? [...prev, companyId]
        : prev.filter((checkedId) => checkedId !== companyId),
    );
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage("");
    try {
      const {
        data: { data },
      } = await updatePricebookBranches(
        pricebookUuid,
        buildBranchesRequest(branchScope, checkedCompanyIds, branchList),
      );
      if (data) onSaved(data);
      onClose();
    } catch (error) {
      const refusal = readPricebookBranchesRefusal(error);
      if (!refusal) {
        setErrorMessage(SAVE_FAILED);
        return;
      }
      // Shops were put on those branches since the dialog opened, so they stay ticked.
      const refusedIds = refusal.branchesWithShops.map(
        (branch) => branch.company_id,
      );
      setCheckedCompanyIds((prev) => Array.from(new Set([...prev, ...refusedIds])));
      setErrorMessage(
        buildBranchesWithShopsMessage(refusal.message, refusal.branchesWithShops),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmModal
      open={open}
      title="สาขาที่ใช้สมุดราคานี้"
      confirmLabel="บันทึก"
      loading={saving}
      confirmDisabled={isDraft || !branches}
      onClose={handleClose}
      onConfirm={handleSave}
    >
      <Stack gap={1.5}>
        {isDraft && <Alert severity="info">{DRAFT_NOTICE}</Alert>}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <RadioGroup row value={branchScope} onChange={handleChangeScope}>
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={ALL_BRANCHES_LABEL}
            disabled={isReadOnly}
          />
          <FormControlLabel
            value="selected"
            control={<Radio />}
            label="เลือกสาขา"
            disabled={isReadOnly}
          />
        </RadioGroup>
        {branchScope === "selected" && (
          <Stack
            sx={{
              maxHeight: BRANCH_LIST_MAX_HEIGHT,
              overflowY: "auto",
              border: (theme) => `1px solid ${theme.palette.draft.main}`,
              borderRadius: GROUP_RADIUS,
              px: 1.5,
              py: 0.5,
            }}
          >
            {branchList.map((branch) => (
              <BranchOption
                key={branch.company_id}
                branch={branch}
                isChecked={checkedCompanyIds.includes(branch.company_id)}
                disabled={isReadOnly}
                onToggle={handleToggleBranch}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </ConfirmModal>
  );
}

interface BranchOptionProps {
  branch: PricebookBranch;
  isChecked: boolean;
  disabled: boolean;
  onToggle: (companyId: string, isChecked: boolean) => void;
}

function BranchOption({ branch, isChecked, disabled, onToggle }: BranchOptionProps) {
  const isLocked = isBranchLocked(branch);
  const lockHint = branch.is_head_office ? HEAD_OFFICE_HINT : HAS_SHOPS_HINT;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onToggle(branch.company_id, event.target.checked);

  return (
    <Tooltip title={isLocked ? lockHint : ""} placement="left">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked || isLocked}
              disabled={disabled || isLocked}
              onChange={handleChange}
            />
          }
          label={`${branch.branch_code} ${branch.branch_name}`}
        />
        <Typography variant="body2" color="text.secondary">
          {numberWithCommas(branch.shop_count)} ร้าน
        </Typography>
      </Stack>
    </Tooltip>
  );
}
